import { useCallback, useMemo, useRef, useState } from 'react';
import type { ScreenAction } from '@taki/ui';

/**
 * LES ACTIONS QU'UNE VUE DE FICHE CANVAS POSE DANS LA BARRE DU GABARIT (chantier
 * `actions-vue-fiche`, `docs/socle/actions-vue-fiche.md`).
 *
 * UNE VUE DE FICHE NE PORTE AUCUN BOUTON D'ACTION DANS SON CORPS : ses gestes
 * vont aux deux places que le gabarit prevoit deja, sans nouveau composant de
 * bouton. La RANGEE 3 (`recordActions` de `ScreenLayout`, les gestes visibles,
 * sans plafond, le lime de G14 garanti par le chassis) et le menu « ... » de la
 * fiche (`recordMenuActions`, en tete, avant les gestes du socle). La vue les
 * declare par `poserActions` ; l'ecran les rend, les grise avec leur raison
 * (G16) et les retire quand la vue, la fiche ou l'etat de brouillon changent.
 */

/** UNE ACTION QU'UNE VUE DE FICHE POSE (libelle court, G13). */
export interface ActionDeVueDeFiche {
  id: string;
  /** Un libelle court (G13), jamais construit depuis une donnee. */
  libelle: string;
  /** Defaut `secondary` ; le lime reste unique (G14), le chassis le garantit deja. */
  variante?: 'primary' | 'secondary';
  /** Presente : l'action est grisee, et c'est sa raison (G16). */
  desactivee?: string;
  onClick(): void;
}

/** CE QU'UNE VUE DE FICHE POSE : les gestes visibles de la rangee 3, et le reste pour le menu « ... ». */
export interface ActionsDeVueDeFiche {
  /** Les gestes visibles de la rangee 3 (`recordActions`), tous, sans plafond (decision du fondateur du 28/10/2026). */
  rangee?: readonly ActionDeVueDeFiche[];
  /** Les autres, en tete du menu « ... » de la fiche, avant les gestes du socle. */
  menu?: readonly ActionDeVueDeFiche[];
}

/** QUI A POSE : la vue active et la fiche ouverte. Hors de ce couple, les actions ne valent plus. */
export interface PorteurDActions {
  vue: string;
  fiche: string;
}

/**
 * LA REPARTITION : la rangee et le menu tels que la vue les a poses, sans
 * plafond ni bascule (`docs/socle/actions-fiche-sans-plafond.md`). Un geste
 * declare pour le menu y reste. PURE, exportee pour un test.
 */
export function repartirLesActions(actions: ActionsDeVueDeFiche): {
  rangee: ActionDeVueDeFiche[];
  menu: ActionDeVueDeFiche[];
} {
  return { rangee: [...(actions.rangee ?? [])], menu: [...(actions.menu ?? [])] };
}

/** Ce qui change l'AFFICHAGE d'une action (pas son `onClick`, relu au clic). */
const signatureDe = (action: ActionDeVueDeFiche): string =>
  JSON.stringify([action.id, action.libelle, action.variante ?? 'secondary', action.desactivee ?? null]);

interface Posees {
  porteur: PorteurDActions;
  signature: string;
  rangee: readonly ActionDeVueDeFiche[];
  menu: readonly ActionDeVueDeFiche[];
}

const memePorteur = (a: PorteurDActions, b: PorteurDActions): boolean => a.vue === b.vue && a.fiche === b.fiche;

/** Les entrees du gabarit : la meme `ScreenAction` que partout, rien de neuf a rendre. */
function versLeGabarit(
  actions: readonly ActionDeVueDeFiche[],
  declencher: (id: string) => void,
): ScreenAction[] {
  return actions.map((action): ScreenAction => ({
    id: `vue-de-fiche:${action.id}`,
    label: action.libelle,
    ...(action.variante === undefined ? {} : { variant: action.variante }),
    disabled: action.desactivee !== undefined,
    ...(action.desactivee === undefined ? {} : { disabledReason: action.desactivee }),
    onClick: () => declencher(action.id),
  }));
}

/**
 * L'ETAT DES ACTIONS POSEES PAR LA VUE DE FICHE ACTIVE.
 *
 * - `poser(porteur, actions)` : la vue pose (ou retire, avec `null`) ses
 *   actions. Rappelee a chaque changement d'etat, elle ne rend l'ecran que si
 *   l'AFFICHAGE change (libelle, variante, raison) : le `onClick` le plus
 *   recent est lu au clic, sans nouveau rendu, donc une vue qui repose ses
 *   actions a chaque rendu ne boucle pas.
 * - `rangee` et `menu` ne valent que pour `vueActive` et `ficheOuverte` : une
 *   autre vue (retour a « Fiche »), une autre fiche, un brouillon
 *   (`ficheOuverte` nulle) et elles tombent, sans attendre le demontage.
 */
export function useActionsDeVueDeFiche(
  vueActive: string,
  ficheOuverte: string | null,
): { poser(porteur: PorteurDActions, actions: ActionsDeVueDeFiche | null): void; rangee: ScreenAction[]; menu: ScreenAction[] } {
  const [posees, setPosees] = useState<Posees | null>(null);
  const courantes = useRef<{ porteur: PorteurDActions; parId: Map<string, ActionDeVueDeFiche> } | null>(null);

  const poser = useCallback((porteur: PorteurDActions, actions: ActionsDeVueDeFiche | null): void => {
    if (actions === null) {
      if (courantes.current !== null && memePorteur(courantes.current.porteur, porteur)) courantes.current = null;
      setPosees((avant) => (avant !== null && memePorteur(avant.porteur, porteur) ? null : avant));
      return;
    }
    const { rangee, menu } = repartirLesActions(actions);
    courantes.current = { porteur, parId: new Map([...rangee, ...menu].map((action) => [action.id, action])) };
    const signature = JSON.stringify([rangee.map(signatureDe), menu.map(signatureDe)]);
    setPosees((avant) =>
      avant !== null && memePorteur(avant.porteur, porteur) && avant.signature === signature
        ? avant
        : { porteur, signature, rangee, menu },
    );
  }, []);

  const declencher = useCallback((id: string): void => {
    courantes.current?.parId.get(id)?.onClick();
  }, []);

  const valables = posees !== null && ficheOuverte !== null && posees.porteur.vue === vueActive && posees.porteur.fiche === ficheOuverte
    ? posees
    : null;
  const rangee = useMemo(() => (valables === null ? [] : versLeGabarit(valables.rangee, declencher)), [valables, declencher]);
  const menu = useMemo(() => (valables === null ? [] : versLeGabarit(valables.menu, declencher)), [valables, declencher]);
  return { poser, rangee, menu };
}
